import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Eye, Target, Layers, Users, Monitor, Settings, FileCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.7 } };

const LiveCounter = ({ base = 10489 }) => {
  const [count, setCount] = useState(base);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let frame = 0;
        const duration = 2000;
        const start = performance.now();
        const tick = (now) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * base));
          if (progress < 1) requestAnimationFrame(tick);
          else {
            setCount(base);
            const bump = () => {
              setCount(prev => prev + Math.floor(1 + Math.random() * 3));
              const next = 2000 + Math.random() * 6000;
              ref.current._timeout = setTimeout(bump, next);
            };
            ref.current._timeout = setTimeout(bump, 3000 + Math.random() * 3000);
          }
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });

    if (ref.current) observer.observe(ref.current);
    return () => {
      observer.disconnect();
      if (ref.current?._timeout) clearTimeout(ref.current._timeout);
    };
  }, [base]);

  return <span ref={ref}>{count.toLocaleString()}</span>;
};

const AboutPage = () => {
  const navigate = useNavigate();

  const clients = ["Netflix", "Amazon Prime Video", "Warner Bros. Discovery", "NBCUniversal", "Iyuno", "Deluxe", "Zoo"];

  useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  const pillars = [
    {
      icon: Layers,
      title: "Subtitle Localization",
      items: ["Translation & Adaptation", "SDH / Closed Captions", "Subtitle QC", "Spotting & Timing", "Conforms & Reformatting", "Template Creation"]
    },
    {
      icon: FileCheck,
      title: "English Master Templates",
      items: ["Dialogue Accuracy", "Speaker Identification", "Grammar & Formatting", "Timing Optimization", "Reading Speed Compliance", "Platform Compliance"]
    },
    {
      icon: Settings,
      title: "Media Operations Support",
      items: ["Vendor Management", "Localization Coordination", "Workflow Tracking", "Resource Management", "Metadata Handling", "Delivery Operations"]
    }
  ];

  const philosophy = [
    { title: "Platform-Aware Localization", text: "We understand OTT standards, subtitle guidelines, reading speeds, CPL/CPS limitations, and delivery specifications across major streaming platforms." },
    { title: "Scalability", text: "From single-title deliveries to high-volume episodic workflows, we scale resources quickly while maintaining consistency and quality." },
    { title: "Quality-Focused Operations", text: "Every workflow is built around accuracy, technical compliance, cultural sensitivity, viewer readability, and consistency." },
    { title: "Technology + Human Expertise", text: "We combine operational systems, automation workflows, and experienced language professionals to ensure efficient and reliable deliveries." }
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5]">
      <nav className="fixed top-0 left-0 right-0 z-50 nav-scrolled">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center"><img src="/graphikpro-logo.png" alt="Graphikpro" className="h-8 w-auto" /></Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="mono-font text-xs tracking-[0.2em] text-[#A1A1AA] hover:text-[#17B352] transition-colors uppercase">Home</Link>
            <Link to="/about" className="mono-font text-xs tracking-[0.2em] text-[#17B352] uppercase">About</Link>
            <Link to="/careers" className="mono-font text-xs tracking-[0.2em] text-[#A1A1AA] hover:text-[#17B352] transition-colors uppercase">Careers</Link>
          </div>
        </div>
      </nav>

      <div className="pt-28 pb-20">
        {/* Hero */}
        <section className="max-w-5xl mx-auto px-6 mb-24">
          <motion.div {...fadeUp}>
            <button onClick={() => navigate("/")}
              className="flex items-center gap-2 mono-font text-xs text-[#52525B] hover:text-[#17B352] transition-colors uppercase tracking-wider mb-10">
              <ArrowLeft className="w-4 h-4" /> Home
            </button>
            <p className="mono-font text-xs tracking-[0.3em] text-[#17B352] mb-4 uppercase">About Us</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl italic mb-6 leading-tight">
              Scaling stories<br />beyond language.
            </h1>
            <p className="text-lg md:text-xl text-[#A1A1AA] max-w-2xl leading-relaxed">
              Graphikpro Private Limited is a modern media localization and content solutions company
              based in Puducherry, India, delivering high-quality language, subtitle, and digital media
              services for global entertainment platforms, studios, agencies, and brands.
            </p>
          </motion.div>
        </section>

        {/* Vision & Mission */}
        <section className="max-w-5xl mx-auto px-6 mb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div {...fadeUp} className="glass-card rounded-xl p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[rgba(23,179,82,0.1)]">
                  <Eye className="w-5 h-5 text-[#17B352]" />
                </div>
                <h2 className="text-2xl">Vision</h2>
              </div>
              <p className="text-[#A1A1AA] leading-relaxed">
                To become a globally trusted media localization and content infrastructure partner
                for the next generation of entertainment, streaming, and digital-first companies.
              </p>
            </motion.div>

            <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.15 }} className="glass-card rounded-xl p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[rgba(0,122,255,0.1)]">
                  <Target className="w-5 h-5 text-[#007AFF]" />
                </div>
                <h2 className="text-2xl">Mission</h2>
              </div>
              <ul className="space-y-3 text-[#A1A1AA]">
                <li className="flex items-start gap-2">
                  <span className="text-[#17B352] mt-1.5 text-xs">&#9670;</span>
                  Deliver world-class localization and subtitle services at scale
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#17B352] mt-1.5 text-xs">&#9670;</span>
                  Bridge language barriers through culturally adaptive content
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#17B352] mt-1.5 text-xs">&#9670;</span>
                  Build efficient and scalable media workflows for global distribution
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#17B352] mt-1.5 text-xs">&#9670;</span>
                  Combine technology, creativity, and operational excellence under one ecosystem
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Founder's Note */}
        <section className="max-w-5xl mx-auto px-6 mb-24">
          <motion.div {...fadeUp} className="relative">
            <div className="absolute top-0 left-0 text-[6rem] leading-none text-[#17B352] opacity-10" style={{ fontFamily: "'Instrument Serif', serif" }}>"</div>
            <div className="pl-6 md:pl-16 pt-10">
              <p className="text-xl md:text-2xl text-[#E5E5E5] leading-relaxed display-font italic mb-6 max-w-3xl">
                Graphikpro was built with a simple vision — to create a modern media company
                that understands both creativity and operations. What started as a small freelance
                journey evolved into a growing global localization ecosystem focused on quality,
                scalability, and long-term partnerships.
              </p>
              <p className="text-lg text-[#A1A1AA] leading-relaxed display-font italic mb-8 max-w-3xl">
                Today, our goal is not just to deliver subtitles, but to help stories travel
                across cultures, languages, and audiences worldwide.
              </p>
              <div>
                <p className="text-[#E5E5E5] font-medium">R Mathiyazhagan</p>
                <p className="mono-font text-xs text-[#52525B] uppercase tracking-wider">Founder & Managing Director</p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Core Service Pillars */}
        <section id="services" className="max-w-5xl mx-auto px-6 mb-24">
          <motion.div {...fadeUp} className="mb-10">
            <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-3 uppercase">Integrated Pillars</p>
            <h2 className="text-3xl md:text-4xl">Core Services</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {pillars.map((pillar, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.7, delay: i * 0.12 }}
                className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[rgba(23,179,82,0.08)]">
                    <pillar.icon className="w-4 h-4 text-[#17B352]" />
                  </div>
                  <h3 className="text-lg">{pillar.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {pillar.items.map((item, j) => (
                    <span key={j} className="mono-font text-[11px] px-2.5 py-1 rounded-full border border-[rgba(255,255,255,0.07)] text-[#A1A1AA] bg-[rgba(255,255,255,0.02)]">
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Philosophy */}
        <section className="max-w-5xl mx-auto px-6 mb-24">
          <motion.div {...fadeUp} className="mb-10">
            <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-3 uppercase">Our Approach</p>
            <h2 className="text-3xl md:text-4xl">What Makes Us Different</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {philosophy.map((p, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.7, delay: i * 0.1 }}
                className="border-l-2 border-[#1f1f23] pl-5 py-1">
                <h3 className="text-lg mb-2">{p.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Clients */}
        <section className="max-w-5xl mx-auto px-6 mb-24">
          <motion.div {...fadeUp} className="text-center">
            <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-3 uppercase">Trusted By</p>
            <h2 className="text-3xl md:text-4xl">Global Partners</h2>
          </motion.div>
        </section>

        {/* Stats & Info */}
        <section className="max-w-5xl mx-auto px-6 mb-24">
          <motion.div {...fadeUp} className="glass-card rounded-xl p-8 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#17B352] mb-2 italic" style={{ fontFamily: "'Instrument Serif', serif" }}>10+</div>
                <p className="mono-font text-xs text-[#52525B] uppercase tracking-wider">Years of Experience</p>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#17B352] mb-2 italic" style={{ fontFamily: "'Instrument Serif', serif" }}>50+</div>
                <p className="mono-font text-xs text-[#52525B] uppercase tracking-wider">Languages Supported</p>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#17B352] mb-2 italic" style={{ fontFamily: "'Instrument Serif', serif" }}><LiveCounter base={10489} /></div>
                <p className="mono-font text-xs text-[#52525B] uppercase tracking-wider">Projects Delivered</p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Contact */}
        <section className="max-w-5xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center">
            <h2 className="text-3xl md:text-4xl italic mb-4">Ready to localize?</h2>
            <p className="text-[#A1A1AA] mb-2">
              <a href="mailto:askus@graphikpro.com" className="text-[#17B352] hover:underline">askus@graphikpro.com</a>
            </p>
            <p className="mono-font text-xs text-[#52525B]">www.graphikpro.com</p>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
